// "Erscheinungsbild"-Gruppe der Settings-Navigation: Theme, Layout-Stil,
// Zeilenhöhe. Ausgelagert aus routes/Settings.tsx (Redesign: 15 Abschnitte
// in Tabs gruppiert) nur um die Hauptdatei unter der 500-Zeilen-Grenze zu
// halten — wortgleiche Verschiebung, Logik bleibt in Settings.tsx, hier nur
// Darstellung + Weiterreichen der Klick-Handler. Ausnahme: der Schalter
// „Neues Design" (DesignToggle) speichert selbst, weil er serverseitig pro
// Benutzer gilt und auch ohne geladene Settings-Prefs funktionieren muss.

import { useState } from "preact/hooks";
import { Sun, Moon, Monitor, MoonStar } from "lucide-preact";
import type { UserPreferences } from "@juuplan/shared";
import {
  beginDesignWrite,
  designSignal,
  endDesignWrite,
  saveDesignChoice,
  type DesignChoice,
  type ThemeChoice,
} from "../../lib/theme";
import { api } from "../../lib/api";
import { setMeDesign, useMe } from "../../lib/auth";
import { setUserPrefs } from "../../store/userPrefs";
import { toastError, toastSuccess } from "../../lib/toast";
import type { Density } from "../../lib/density";
import type { LayoutStyle } from "../../lib/layout";
import { ThemeCard, DensityButton } from "./SettingsControls";
import { Switch } from "../../components/ui/Switch";

export interface AppearanceGroupProps {
  theme: ThemeChoice;
  onTheme: (next: ThemeChoice) => void;
  layout: LayoutStyle;
  onLayout: (next: LayoutStyle) => void;
  density: Density;
  onDensity: (next: Density) => void;
}

/** Ein/Aus-Schalter „Neues Design" — gespeichert pro Benutzer auf dem Server
 *  (PATCH /api/me/preferences, Feld ui_design). Optimistisch: sofort
 *  umschalten, bei Fehler zuruecknehmen und melden. */
function DesignToggle(): preact.JSX.Element {
  const { user } = useMe();
  // Globaler Zustand statt einmalig gelesenem Wert: aendert /api/me das Design
  // (z. B. Tab-Fokus nach Umschalten auf einem anderen Geraet), zieht der
  // Schalter mit. Waehrend eines eigenen PATCH ignoriert theme.ts /api/me.
  const design = designSignal.value;
  const [saving, setSaving] = useState(false);

  const onChange = (on: boolean): void => {
    if (!user) return;
    const previous = design;
    const next: DesignChoice = on ? "juunit" : "classic";
    if (next === previous) return;
    saveDesignChoice(user.id, next);
    setSaving(true);
    beginDesignWrite();
    void (async () => {
      try {
        // silentError: eigener, verstaendlicher Fehler-Toast statt des
        // generischen aus api().
        const result = await api<UserPreferences>("/api/me/preferences", {
          method: "PATCH",
          body: { ui_design: next },
          silentError: true,
        });
        setMeDesign(next);
        setUserPrefs(result);
        toastSuccess(next === "juunit" ? "Neues Design eingeschaltet" : "Bisheriges Design wiederhergestellt");
      } catch {
        saveDesignChoice(user.id, previous);
        toastError("Design konnte nicht gespeichert werden — bitte erneut versuchen.");
      } finally {
        endDesignWrite();
        setSaving(false);
      }
    })();
  };

  return (
    <div class="flex items-center gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] py-2 pl-4 pr-2 sm:py-3 sm:pr-4">
      <div class="min-w-0 flex-1">
        <label
          for="design-switch"
          id="design-switch-label"
          class="block text-sm font-medium text-[color:var(--color-on-surface)] cursor-pointer"
        >
          Neues Design
        </label>
        <p id="design-switch-hint" class="mt-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
          Neue Farben, Schrift und dunkle Seitenleiste. Gilt für dein Konto auf allen Geräten;
          ausgeschaltet siehst du das bisherige Design.
        </p>
      </div>
      <Switch
        id="design-switch"
        aria-labelledby="design-switch-label"
        aria-describedby="design-switch-hint"
        checked={design === "juunit"}
        onChange={onChange}
        disabled={!user || saving}
      />
    </div>
  );
}

export function AppearanceGroup(props: AppearanceGroupProps): preact.JSX.Element {
  return (
    <div class="flex flex-col gap-8">
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Design
        </h2>
        <DesignToggle />
      </section>

      {/* Theme picker — works without prefs (localStorage-backed). */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Erscheinungsbild
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
          <ThemeCard
            active={props.theme === "light"}
            onClick={() => props.onTheme("light")}
            icon={<Sun size={20} aria-hidden="true" />}
            label="Light"
            description="Helles Standard-Theme"
          />
          <ThemeCard
            active={props.theme === "dark"}
            onClick={() => props.onTheme("dark")}
            icon={<Moon size={20} aria-hidden="true" />}
            label="Dark"
            description="Dunkel mit weichem Kontrast"
          />
          <ThemeCard
            active={props.theme === "night"}
            onClick={() => props.onTheme("night")}
            icon={<MoonStar size={20} aria-hidden="true" />}
            label="Night"
            description="OLED-Schwarz für späte Stunden"
          />
          <ThemeCard
            active={props.theme === "system"}
            onClick={() => props.onTheme("system")}
            icon={<Monitor size={20} aria-hidden="true" />}
            label="System"
            description="Folgt OS-Einstellung"
          />
        </div>
      </section>

      {/* Layout-Stil — Komfort (Default, Asana-inspiriert) vs Kompakt
          (klassischer Monday-Look mit gefüllten Status-Cells). */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Layout-Stil
        </h2>
        <div class="flex flex-wrap max-w-full rounded border border-[color:var(--color-outline-variant)] overflow-hidden">
          <DensityButton
            active={props.layout === "komfort"}
            onClick={() => props.onLayout("komfort")}
            label="juuplan-Komfort"
          />
          <DensityButton
            active={props.layout === "kompakt"}
            onClick={() => props.onLayout("kompakt")}
            label="juuplan-Kompakt"
          />
        </div>
        <p class="mt-2 text-xs text-[color:var(--color-on-surface-variant)]">
          Komfort: luftige Zeilen, Status als Pill, weiße Cells. Kompakt: gefüllte
          Status-Cells in dichten Reihen (klassischer Look).
        </p>
      </section>

      {/* Density picker — single row of three radio-style buttons. */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Zeilenhöhe
        </h2>
        <div class="flex flex-wrap max-w-full rounded border border-[color:var(--color-outline-variant)] overflow-hidden">
          <DensityButton
            active={props.density === "compact"}
            onClick={() => props.onDensity("compact")}
            label="Kompakt"
          />
          <DensityButton
            active={props.density === "comfortable"}
            onClick={() => props.onDensity("comfortable")}
            label="Standard"
          />
          <DensityButton
            active={props.density === "cozy"}
            onClick={() => props.onDensity("cozy")}
            label="Geräumig"
          />
        </div>
        <p class="mt-2 text-xs text-[color:var(--color-on-surface-variant)]">
          Wirkt sich auf alle Tabellen-Sichten aus.
        </p>
      </section>
    </div>
  );
}
